// libraries
import React, { useState, useEffect } from "react";
import Scrollbars from "rc-scrollbars";
import { useHistory, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AppCustomToast, { PageContext } from "components/app-custom-toast";
// routes
import pathnames from "routes/pathnames";
// components
import AppNav from "components/app-nav";
import SessionTimeoutModal from "components/session-timeout-modal";
import SessionExpiredModal from "components/session-expired-modal";
// hooks
import useIdleTimeout from "hooks/use-idle-timeout";
// pages
import PageLogout from "pages/logout";

const IDLE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
const WARNING_TIMEOUT_MS = 60 * 1000; // 60 seconds countdown

const formatInactiveDuration = (ms) => {
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.round((ms % 60000) / 1000);
  if (minutes > 0) return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  return `${seconds} second${seconds === 1 ? "" : "s"}`;
};

const INACTIVE_DURATION = formatInactiveDuration(IDLE_TIMEOUT_MS);

const AppLayout = ({ children }) => {
  const history = useHistory();
  const location = useLocation();
  const [showLogout, setShowLogout] = useState(false);
  const [showIndicator, setShowIndicator] = useState({
    isOpen: false,
    condition: "",
    title: "",
    message: "",
    defaultOnClick: true,
    onClick: () => null,
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const logoutToggle = () => {
    setShowLogout(!showLogout);
  };

  const logout = () => {
    localStorage.clear();
    setShowLogout(false);
    history.push(pathnames.pageLogin);
  };

  const handleSessionExpired = () => {
    localStorage.clear();
    setShowLogout(false);
    setSessionExpired(true);
  };

  const handleExpiredOk = () => {
    setSessionExpired(false);
    history.push(pathnames.pageLogin);
  };

  const { showWarning, remainingSeconds, resetTimer } = useIdleTimeout({
    onIdle: handleSessionExpired,
    idleTime: IDLE_TIMEOUT_MS,
    warningTime: WARNING_TIMEOUT_MS,
    active: isAuthenticated && !sessionExpired,
  });

  const handleSessionLogout = () => {
    resetTimer();
    logout();
  };

  useEffect(() => {
    const authenticated = localStorage.getItem("@storage_token");
    if (authenticated) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }
  }, [location]);

  const getScrollBarsClassName = (isAuthenticated) => {
    const classNames = ["rc-scrollbars"];
    if (isAuthenticated) classNames.push("rc-scrollbars__with-navbar");
    return classNames.join(" ");
  };

  return (
    <PageContext.Provider value={[showIndicator, setShowIndicator]}>
      <div className="root-page app-layout">
        <ToastContainer />

        <main className="root-main">
          {isAuthenticated && <AppNav logoutToggle={logoutToggle} />}

          <Scrollbars id="rc-scroll" className={getScrollBarsClassName(isAuthenticated)}>
            {children}
          </Scrollbars>
        </main>
        <PageLogout isOpen={showLogout} toggle={logoutToggle} logout={logout} />
        <SessionTimeoutModal
          isOpen={showWarning}
          secondsLeft={remainingSeconds}
          onStayLoggedIn={resetTimer}
          onLogoutNow={handleSessionLogout}
        />
        <SessionExpiredModal
          isOpen={sessionExpired}
          inactiveDuration={INACTIVE_DURATION}
          onOk={handleExpiredOk}
        />

        <AppCustomToast showIndicator={showIndicator} reintIndicator={setShowIndicator}></AppCustomToast>
      </div>
    </PageContext.Provider>
  );
};

export default AppLayout;
